import {
  isDolomite,
  isExactly,
  isFlyingTulip,
  isLiquityFamily,
  isRiver,
  isTeller,
  isTermMax,
  isInverse,
  isCooler,
  isLlamaLend,
  isResupply,
  isCurvance,
  isTwyne,
  isFraxlend,
  isDssType,
  isFrankencoin,
  isMidnight,
  isTerm,
  isMorphoType,
  isMultiMarket,
  isSiloV2Type,
  isSiloV3Type,
} from '@1delta/lender-registry'
import { AdditionalYields } from '../../types'
import { fetchMorphoMarkets } from './morpho/fetchPublic'
import { convertMarketsToMorphoResponse } from './morpho/convertPublic'
import { fetchMidnightMarkets } from './midnight/fetchPublic'
import { convertMidnightMarketsToResponse } from './midnight/convertPublic'
import { fetchTermMarkets } from './term/fetchPublic'
import { convertTermMarketsToResponse } from './term/convertPublic'
import { fetchExactlyMarkets } from './exactly/fetchPublic'
import { convertExactlyMarketsToResponse } from './exactly/convertPublic'
import { fetchFlyingTulipMarkets } from './flying-tulip/fetchPublic'
import { convertFlyingTulipMarketsToResponse } from './flying-tulip/convertPublic'
import { fetchLiquityMarkets } from './liquity/fetchPublic'
import { convertLiquityMarketsToResponse } from './liquity/convertPublic'
import { fetchRiverMarkets } from './river/fetchPublic'
import { convertRiverMarketsToResponse } from './river/convertPublic'
import { fetchTellerMarkets } from './teller/fetchPublic'
import { fetchTermMaxMarkets } from './termmax/fetchPublic'
import { convertTellerMarketsToResponse } from './teller/convertPublic'
import { convertTermMaxMarketsToResponse } from './termmax/convertPublic'
import { fetchInverseMarkets } from './inverse/fetchPublic'
import { fetchCoolerMarkets } from './cooler/fetchPublic'
import { fetchLlamaLendMarkets } from './llamalend/fetchPublic'
import { fetchResupplyMarkets } from './resupply/fetchPublic'
import { fetchCurvanceMarkets } from './curvance/fetchPublic'
import { fetchTwyneMarkets } from './twyne/fetchPublic'
import { fetchFraxlendPairs } from './fraxlend/fetchPublic'
import { convertInverseMarketsToResponse } from './inverse/convertPublic'
import { convertCoolerMarketsToResponse } from './cooler/convertPublic'
import { convertLlamaLendMarketsToResponse } from './llamalend/convertPublic'
import { convertResupplyMarketsToResponse } from './resupply/convertPublic'
import { convertCurvanceMarketsToResponse } from './curvance/convertPublic'
import { convertTwyneMarketsToResponse } from './twyne/convertPublic'
import { convertFraxlendPairsToResponse } from './fraxlend/convertPublic'
import { fetchDssMarkets } from './dss/fetchPublic'
import { convertDssMarketsToResponse } from './dss/convertPublic'
import { fetchFrankencoinMarkets } from './frankencoin/fetchPublic'
import { convertFrankencoinMarketsToResponse } from './frankencoin/convertPublic'
import { fetchSiloMarkets } from './silo-v2/fetchPublic'
import { convertSiloMarketsToPublicResponse } from './silo-v2/convertPublic'
import { convertSiloV3MarketsToPublicResponse } from './silo-v3/convertPublic'
import {
  fetchDolomiteMarketsFromSubgraph,
  convertDolomiteSubgraphMarkets,
} from './dolomite/fetchFromSubgraph'
import { GenericTokenList } from '../../types/general'

// Toggle to silence lending-pipeline warnings. Set to `true` (or wire to an
// env flag) when debugging fetch/convert failures.
const LENDING_DEBUG = false
const warn = (...args: unknown[]) => {
  if (LENDING_DEBUG) console.warn(...args)
}

async function getLenderDataFromApi(
  lender: string,
  chainId: string,
  prices: { [a: string]: number },
  additionalYields: AdditionalYields,
  includeUnlisted = false,
) {
  if (isMorphoType(lender))
    return await fetchMorphoMarkets(chainId, includeUnlisted)
  // Midnight: one book fetch per configured market on the chain; the bare
  // `MORPHO_MIDNIGHT` key expands to per-market `MORPHO_MIDNIGHT_<id>` keys in
  // the converter (multi-market, like Morpho).
  if (isMidnight(lender)) return await fetchMidnightMarkets(chainId)
  // Term Finance: static repo config × live subgraph listings/auction top; the
  // bare `TERM_FINANCE` key expands to per-repo `TERM_FINANCE_<id>` keys in the
  // converter (multi-market, like Midnight).
  if (isTerm(lender)) return await fetchTermMarkets(chainId)
  // Exactly: ONE on-chain Previewer call per chain (no API/indexer at all); the
  // bare `EXACTLY` key expands to per-market `EXACTLY_<MARKET_ADDR>` keys in
  // the converter (multi-market, like Midnight/Term).
  if (isExactly(lender)) return await fetchExactlyMarkets(chainId)
  // Flying Tulip: two ON-CHAIN LendingLens multicall rounds per chain (no API
  // exists — api.flyingtulip.com is analytics). NOT multi-market:
  // `FLYING_TULIP` is both the dispatch key and the market key (cross-margin,
  // one PositionsManager per chain), one entry per asset inside it.
  if (isFlyingTulip(lender)) return await fetchFlyingTulipMarkets(chainId)
  // Liquity family: one ON-CHAIN multicall per deployment (the bare lender key
  // selects the config row); expands to per-branch `<KEY>_<chainId>_<COLL_INDEX>` keys
  // in the converter (multi-market, like Midnight/Term/Exactly).
  if (isLiquityFamily(lender)) return await fetchLiquityMarkets(lender, chainId)
  // River: one ON-CHAIN multicall per chain; expands to per-TroveManager
  // `RIVER_<chainId>_<TM_INDEX>` keys in the converter (multi-market).
  if (isRiver(lender)) return await fetchRiverMarkets(lender, chainId)
  // Teller: one ON-CHAIN multicall over the curated pool list per chain; expands
  // to per-pool `TELLER_<POOL_ADDR>` keys in the converter (multi-market).
  if (isTeller(lender)) return await fetchTellerMarkets(chainId)
  // Inverse FiRM: hybrid API-first + on-chain fallback; expands to per-market
  // `INVERSE_<MARKET_ADDR>` keys in the converter (multi-market).
  if (isInverse(lender)) return await fetchInverseMarkets(lender, chainId)
  // Cooler (Olympus V2): ONE on-chain multicall over the single MonoCooler
  // book — no API exists anywhere in the path. NOT multi-market: `COOLER` is
  // both the dispatch key and the market key (one market ever, like plain
  // Aave), so the converter's map is unwrapped in the branch below.
  if (isCooler(lender)) return await fetchCoolerMarkets(lender, chainId)
  // LlamaLend: hybrid — ONE `GET /v1/getLendingVaults/all` covers every chain
  // and BOTH generations, with an on-chain multicall fallback. Expands to
  // per-market `LLAMALEND_<CONTROLLER_ADDR>` keys in the converter
  // (multi-market). The roster comes from lender-metadata, never from the API.
  if (isLlamaLend(lender)) return await fetchLlamaLendMarkets(lender, chainId)
  // Resupply: registry discovery + one ON-CHAIN batch per chain; expands to
  // per-pair `RESUPPLY_<chainId>_<PAIR_ADDR>` keys in the converter
  // (multi-market). No API and no published roster anywhere in the path.
  if (isResupply(lender)) return await fetchResupplyMarkets(lender, chainId)
  // Curvance: CentralRegistry discovery + the ProtocolReader's two calls (with
  // a plain-multicall fallback); expands to per-market
  // `CURVANCE_<chainId>_<MARKET_MANAGER>` keys in the converter (multi-market).
  // No API anywhere in the path — `api.curvance.com` is a points backend and
  // its only market route serves Monad TESTNET.
  if (isCurvance(lender)) return await fetchCurvanceMarkets(lender, chainId)
  // Twyne: ONE on-chain batch over the curated `(intermediateVault, targetVault,
  // targetAsset)` roster from lender-metadata; expands to per-market
  // `TWYNE_<chainId>_<IV>_<TARGET_ASSET>` keys in the converter (multi-market).
  // There is no discovery step — `VaultManager`'s whitelists are un-enumerable
  // mappings, so the roster is generated from its EVENTS and this path fails
  // closed on anything absent from it.
  if (isTwyne(lender)) return await fetchTwyneMarkets(lender, chainId)
  // Fraxlend: NO discovery — one ON-CHAIN batch over the CURATED pair
  // allowlist from data-sdk; expands to per-pair `FRAXLEND_<chainId>_<PAIR>`
  // keys in the converter (multi-market). Deliberately never enumerates the
  // deployer: it mixes in 12 FraxlendV1 contracts (which revert on
  // `depositLimit`) and dozens of dust pairs, and the other mainnet registry
  // is 62 Peapods pod-token pairs. No API and no subgraph exist.
  if (isFraxlend(lender)) return await fetchFraxlendPairs(lender, chainId)
  // TermMax: hybrid — ONE `GET /market/data?chainId=` per chain returns every
  // live market plus its order book; expands to per-market `TERMMAX_<MARKET_ADDR>`
  // keys in the converter (multi-market). Markets are DISCOVERED, never read
  // from a static registry (they roll with every maturity).
  //
  // MATURED MARKETS ARE INCLUDED. They cannot be entered, and the converter
  // already renders them inert — zero rates, zero liquidity, `depositsEnabled`
  // and `borrowingEnabled` false — so they add no actionable surface. They are
  // here because the USER-data converter renders a position against its
  // market's meta row, and without a row a holder who never redeemed is
  // invisible: measured 2026-08-21, ~1.24M USDC of unredeemed FT sits across
  // 306 matured Ethereum markets, including a single ~998k position eight
  // months past maturity. The set is bounded by the residue sweep, which drops
  // the matured markets that are fully settled (683 → 306 on Ethereum).
  if (isTermMax(lender))
    return await fetchTermMaxMarkets(chainId, undefined, {
      includeMatured: true,
    })
  // dss (Sky + the USDD fork): one ON-CHAIN multicall over the metadata ilk
  // roster per chain; expands to per-ilk `<BRAND>_<chainId>_<ILK>` keys in the
  // converter (multi-market). An empty roster returns zero markets cheaply.
  if (isDssType(lender)) return await fetchDssMarkets(lender, chainId)
  // Frankencoin: one ON-CHAIN multicall over the curated originals; expands
  // to per-market `FRANKENCOIN_<chainId>_<ORIGINAL>` keys in the converter.
  if (isFrankencoin(lender))
    return await fetchFrankencoinMarkets(lender, chainId)
  return {}
}

function convertLenderDataFromApi(
  lender: string,
  chainId: string,
  data: any,
  prices: { [a: string]: number },
  additionalYields: AdditionalYields,
  list: GenericTokenList = {},
) {
  if (isMorphoType(lender))
    return convertMarketsToMorphoResponse(data, chainId, additionalYields, list)
  if (isMidnight(lender))
    return convertMidnightMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isTerm(lender))
    return convertTermMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isExactly(lender))
    return convertExactlyMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isLiquityFamily(lender))
    return convertLiquityMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isRiver(lender))
    return convertRiverMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isTeller(lender))
    return convertTellerMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isInverse(lender))
    return convertInverseMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  // Flying Tulip is deliberately NOT `isMultiMarket` (cross-margin — no
  // per-market key space), so unwrap the single-entry map like Cooler below.
  if (isFlyingTulip(lender))
    return (
      convertFlyingTulipMarketsToResponse(
        data,
        chainId,
        prices,
        additionalYields,
        list,
      )[lender] ?? {}
    )
  // Cooler is deliberately NOT `isMultiMarket` (one market ever), so the
  // caller assigns this return value under the bare `COOLER` key directly —
  // unwrap the converter's single-entry map here rather than nesting it.
  if (isCooler(lender))
    return (
      convertCoolerMarketsToResponse(
        data,
        chainId,
        prices,
        additionalYields,
        list,
      )[lender] ?? {}
    )
  if (isLlamaLend(lender))
    return convertLlamaLendMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isResupply(lender))
    return convertResupplyMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isTwyne(lender))
    return convertTwyneMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isCurvance(lender))
    return convertCurvanceMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isFraxlend(lender))
    return convertFraxlendPairsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isFrankencoin(lender))
    return convertFrankencoinMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isDssType(lender))
    return convertDssMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  if (isTermMax(lender))
    return convertTermMaxMarketsToResponse(
      data,
      chainId,
      prices,
      additionalYields,
      list,
    )
  return {}
}

export const getLenderPublicDataViaApi = async (
  chainId: string,
  lenders: string[],
  prices: { [asset: string]: number },
  additionalYields: AdditionalYields,
  tokenList: () => Promise<GenericTokenList> = async () => {
    return {}
  },
  includeUnlisted = false,
): Promise<{ [lender: string]: any }> => {
  // Silo v2/v3 each use a single GraphQL call per chain that returns every
  // pair for that protocol version, so the per-lender loop below would
  // issue one HTTP call per pair. Split them off and fetch once per
  // version; results are per-pair lender keys (`SILO_V2_<HEX>` /
  // `SILO_V3_<HEX>`) which we merge back at the end.
  const siloV2Lenders = lenders.filter((l) => isSiloV2Type(l))
  const siloV3Lenders = lenders.filter((l) => isSiloV3Type(l))
  // Dolomite: one GraphQL call per chain returns every market (single `DOLOMITE`
  // key), same single-query shape as Silo. Split off so the per-lender loop
  // doesn't issue redundant calls.
  const dolomiteLenders = lenders.filter((l) => isDolomite(l))
  const otherLenders = lenders.filter(
    (l) => !isSiloV2Type(l) && !isSiloV3Type(l) && !isDolomite(l),
  )

  const siloV2Promise: Promise<{ [lender: string]: any }> =
    siloV2Lenders.length > 0
      ? fetchSiloMarkets(chainId, { protocolVersion: 'v2' }).then((items) => ({
          __silo_items__: items,
        }))
      : Promise.resolve({})

  const siloV3Promise: Promise<{ [lender: string]: any }> =
    siloV3Lenders.length > 0
      ? fetchSiloMarkets(chainId, { protocolVersion: 'v3' }).then((items) => ({
          __silo_items__: items,
        }))
      : Promise.resolve({})

  let promises: any[] = []
  for (const lender of otherLenders) {
    promises.push(
      getLenderDataFromApi(
        lender,
        chainId,
        prices,
        additionalYields,
        includeUnlisted,
      ),
    )
  }

  // Dolomite single-query promise (appended LAST so otherLenders' settled[i+3]
  // indices stay valid).
  const dolomitePromise: Promise<any> =
    dolomiteLenders.length > 0
      ? fetchDolomiteMarketsFromSubgraph(chainId)
      : Promise.resolve(null)

  const settled = await Promise.allSettled([
    tokenList(),
    siloV2Promise,
    siloV3Promise,
    ...promises,
    dolomitePromise,
  ])

  const listResult = settled[0]
  const list = listResult.status === 'fulfilled' ? listResult.value : {}
  if (listResult.status === 'rejected') {
    warn(
      `[lending] tokenList fetch failed on chain ${chainId}:`,
      listResult.reason,
    )
  }

  const lenderData: { [lender: string]: any } = {}

  // 1a. Silo v2: one GraphQL result, many per-pair lender keys.
  const siloV2Result = settled[1]
  if (siloV2Lenders.length > 0) {
    if (siloV2Result.status === 'fulfilled') {
      const items = (siloV2Result.value as any)?.__silo_items__ ?? []
      try {
        const converted = convertSiloMarketsToPublicResponse(
          items,
          chainId,
          prices,
          additionalYields,
          list,
        )
        for (const lk of siloV2Lenders) {
          if (converted[lk]) lenderData[lk] = converted[lk]
        }
      } catch (e) {
        warn(
          `[lending] Silo v2 API convert failed on chain ${chainId}:`,
          (e as any)?.message ?? e,
        )
      }
    } else {
      warn(
        `[lending] Silo v2 API fetch failed on chain ${chainId}:`,
        siloV2Result.reason?.message ?? siloV2Result.reason,
      )
    }
  }

  // 1b. Silo v3: same shape, different protocol-version filter.
  const siloV3Result = settled[2]
  if (siloV3Lenders.length > 0) {
    if (siloV3Result.status === 'fulfilled') {
      const items = (siloV3Result.value as any)?.__silo_items__ ?? []
      try {
        const converted = convertSiloV3MarketsToPublicResponse(
          items,
          chainId,
          prices,
          additionalYields,
          list,
        )
        for (const lk of siloV3Lenders) {
          if (converted[lk]) lenderData[lk] = converted[lk]
        }
      } catch (e) {
        warn(
          `[lending] Silo v3 API convert failed on chain ${chainId}:`,
          (e as any)?.message ?? e,
        )
      }
    } else {
      warn(
        `[lending] Silo v3 API fetch failed on chain ${chainId}:`,
        siloV3Result.reason?.message ?? siloV3Result.reason,
      )
    }
  }

  // 2. Morpho / Lista / other per-lender API calls.
  for (let i = 0; i < otherLenders.length; i++) {
    try {
      const result = settled[i + 3]
      if (result.status === 'rejected') {
        warn(
          `[lending] API fetch failed for ${otherLenders[i]} on chain ${chainId}:`,
          result.reason?.message ?? result.reason,
        )
        continue
      }
      const lender = otherLenders[i]
      if (isMultiMarket(lender)) {
        const dataObtained = result.value
        const converted: any = convertLenderDataFromApi(
          lender,
          chainId,
          dataObtained,
          prices,
          additionalYields,
          list,
        )
        Object.assign(lenderData, converted)
      } else {
        lenderData[lender] = convertLenderDataFromApi(
          lender,
          chainId,
          result.value,
          prices,
          additionalYields,
          list,
        )
      }
    } catch (e) {
      warn('error fetching from API on', chainId, ':', e)
    }
  }

  // 3. Dolomite: one subgraph result → single `DOLOMITE` key. A null result
  //    (subgraph down / chain has no endpoint) simply omits the key, so the
  //    caller's on-chain fallback runs.
  if (dolomiteLenders.length > 0) {
    const dolomiteResult = settled[settled.length - 1]
    if (dolomiteResult.status === 'fulfilled' && dolomiteResult.value) {
      try {
        const converted = convertDolomiteSubgraphMarkets(
          dolomiteResult.value,
          chainId,
          prices,
          additionalYields,
          list,
        )
        for (const lk of dolomiteLenders) lenderData[lk] = converted
      } catch (e) {
        warn(
          `[lending] Dolomite subgraph convert failed on chain ${chainId}:`,
          (e as any)?.message ?? e,
        )
      }
    } else if (dolomiteResult.status === 'rejected') {
      warn(
        `[lending] Dolomite subgraph fetch failed on chain ${chainId}:`,
        (dolomiteResult as any).reason?.message ??
          (dolomiteResult as any).reason,
      )
    }
  }

  return lenderData
}
